import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Upload, CheckCircle, Loader2, Smartphone, ImagePlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PhotoBridgePage = () => {
  const { sessionId } = useParams();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const res = await axios.post(`${API_URL}/api/photo-bridge/${sessionId}/upload`, formData);
      const newUrls = res.data.urls || [];
      setPhotos(prev => [...prev, ...newUrls]);
      setUploadCount(prev => prev + newUrls.length);
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      {/* Header */}
      <div className="bg-[#0F172A] border-b border-white/10 px-4 py-5 text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <div className="w-8 h-8 border border-[#E07A3A] flex items-center justify-center">
            <span className="text-[#E07A3A] font-['Playfair_Display'] text-lg font-semibold">S</span>
          </div>
          <span className="font-['Playfair_Display'] text-lg text-white tracking-wide">
            SHOPPER<span className="text-[#E07A3A]">DEN</span>
          </span>
        </div>
        <p className="text-white/40 text-xs tracking-widest uppercase">Photo Bridge</p>
      </div>

      {/* Main */}
      <div className="flex-1 px-4 py-8">
        <div className="max-w-sm mx-auto space-y-6">
          {/* Status */}
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#E07A3A]/10 flex items-center justify-center rounded-full">
              <Smartphone className="h-8 w-8 text-[#E07A3A]" />
            </div>
            <h1 className="text-white text-xl font-semibold mb-1">Upload Photos</h1>
            <p className="text-white/50 text-sm">
              Photos will appear on your desktop listing form instantly
            </p>
          </div>

          {/* Upload Count */}
          {uploadCount > 0 && (
            <div className="flex items-center justify-center gap-2 bg-green-500/10 border border-green-500/20 py-3 px-4" data-testid="bridge-upload-count">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span className="text-green-400 font-semibold text-sm">{uploadCount} photo{uploadCount !== 1 ? 's' : ''} sent to desktop</span>
            </div>
          )}

          {/* Upload Buttons */}
          <div className="space-y-3">
            {/* Camera */}
            <input type="file" accept="image/*" capture="environment" ref={cameraRef} onChange={handleUpload} className="hidden" />
            <button
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-3 bg-[#E07A3A] text-white py-4 px-6 font-semibold tracking-wider text-sm hover:bg-[#C4652E] transition-colors disabled:opacity-50"
              data-testid="bridge-camera-btn"
            >
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
              {uploading ? 'Uploading...' : 'Take Photo'}
            </button>

            {/* Gallery */}
            <input type="file" accept="image/*" multiple ref={fileRef} onChange={handleUpload} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-3 bg-white/5 border border-white/10 text-white py-4 px-6 font-semibold tracking-wider text-sm hover:bg-white/10 transition-colors disabled:opacity-50"
              data-testid="bridge-gallery-btn"
            >
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
              {uploading ? 'Uploading...' : 'Choose from Gallery'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          {/* Thumbnails */}
          {photos.length > 0 && (
            <div>
              <p className="text-white/30 text-xs tracking-widest uppercase mb-3">Uploaded</p>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((url) => (
                  <div key={url} className="aspect-square bg-white/5 overflow-hidden">
                    <img src={url.startsWith('/') ? `${API_URL}${url}` : url} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="text-center pt-4">
            <p className="text-white/20 text-xs">
              Session: {sessionId}
            </p>
            <p className="text-white/20 text-xs mt-1">
              You can close this page when done
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoBridgePage;
